/**
 * Client for `Api::LogAdvertisementsController` — shared by video playback logging and
 * (Step 6) image impression POST-only flows. Session via `credentials: 'same-origin'`; CSRF skipped server-side.
 */
const LOG_FLUSH_INTERVAL_MS = 3 * 1000; // short ads (~10–90s): denser mid-play snapshots than 10s; pause/close still flush
const LOG_API_TIMEOUT_MS = 5 * 1000;    // above flush interval so slow-but-OK responses are not aborted early
const LOG_CREATE_MAX_ATTEMPTS = 3;      // POST create: cap avoids hammering the API; only transient failures retry (see below)

/** Inclusive bounds for HTTP 5xx (server error / temporary overload). */
const HTTP_STATUS_SERVER_ERROR_RANGE = Object.freeze({ min: 500, max: 599 });

/**
 * HTTP `status` for POST create: retry only 429 + 5xx (429 = Too Many Requests, not 422 Unprocessable Entity).
 *
 * @param {number} status
 * @returns {boolean}
 */
function isTransientForRetry(status) {
  if (status === 429) return true;

  return status >= HTTP_STATUS_SERVER_ERROR_RANGE.min && status <= HTTP_STATUS_SERVER_ERROR_RANGE.max;
}

/**
 * POST create retry policy (immediate attempts only; no backoff between tries).
 *
 * | Situation | Retry? | Why |
 * |-----------|--------|-----|
 * | `AbortError` (timeout / abort signal) | yes | New request may finish within the limit |
 * | `fetch` → `TypeError` (e.g. "Failed to fetch", transient network) | yes | Same payload often succeeds on the next attempt |
 * | HTTP 429 Too Many Requests | yes | Throttle window may clear between quick attempts |
 * | HTTP 5xx | yes | Server-side fault may be gone on the next attempt |
 * | HTTP 4xx other than 429 (e.g. 401, 403, 422 Unprocessable Entity) | no | Request/session/validation issue; repeating the same POST will not fix it |
 * | `SyntaxError` from JSON.parse | no | Bad or unexpected body; retry will not repair the response |
 * | Anything else | no | Unknown; do not burn attempts |
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isRetryableFailure(error) {
  if (typeof error !== 'object' || error === null) return false;

  if (error.name === 'AbortError') return true;

  if (error instanceof TypeError) return true;

  if ('status' in error && (typeof error.status === 'number') && Number.isFinite(error.status)) {
    return isTransientForRetry(error.status);
  }

  return false;
}

/** Pagehide: pause active modal Vimeo player so pause/flush paths can run (video path only). */
const advertisementLogPageContext = {
  activeVimeoPlayer: null,
  pageHideListenersBound: false,
};

function ensureAdvertisementLogPageHideListeners() {
  if (advertisementLogPageContext.pageHideListenersBound) return;

  advertisementLogPageContext.pageHideListenersBound = true;
  const pauseActive = () => {
    try {
      advertisementLogPageContext.activeVimeoPlayer?.pause();
    } catch (e) {
      // ignore
    }
  };
  window.addEventListener('pagehide', pauseActive);
  window.addEventListener('beforeunload', pauseActive);
}

async function fetchAdvertisementLogResource(method, path, bodyParams) {
  const body = new URLSearchParams();
  Object.entries(bodyParams).forEach(([k, v]) => body.set(k, String(v)));

  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), LOG_API_TIMEOUT_MS);
  try {
    const res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
      },
      body,
      signal: ctrl.signal,
    });

    if (!res.ok) {
      // `res.status` on `err` drives {@link isTransientForRetry} (e.g. 422 no, 429/5xx yes).
      const err = new Error(`${method} ${path} ${res.status}`);
      err.status = res.status;
      throw err;
    }

    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } finally {
    clearTimeout(tid);
  }
}

/**
 * POST `/api/log_advertisements` with bounded immediate retries only when
 * {@link isRetryableFailure} applies (HTTP row: {@link isTransientForRetry}).
 * On success the API returns 2xx JSON with a numeric `log_id`; the client does not handle a null/missing `log_id` on that path.
 * (e.g. video first frame, Step 6 image impression once).
 *
 * @param {string|number} adId `Advertisement` id (same as modal `data-advertisement-id`)
 * @returns {Promise<number|undefined>} server `log_id` when created, else undefined
 */
export async function createAdvertisementLogWithRetries(adId) {
  for (let attempt = 0; attempt < LOG_CREATE_MAX_ATTEMPTS; attempt += 1) {
    try {
      const payload = await fetchAdvertisementLogResource('POST', '/api/log_advertisements', {
        advertisement_id: adId,
      });
      return payload.log_id;

    } catch (e) {
      const lastAttempt = ((attempt + 1) === LOG_CREATE_MAX_ATTEMPTS);
      if (!lastAttempt && isRetryableFailure(e)) continue;

      return undefined;
    }
  }
  return undefined;
}

async function ensureVideoLogRowOnPlayer(vimeoPlayer, adId) {
  if (vimeoPlayer.logId) return;

  if (!vimeoPlayer._logCreatePromise) {
    vimeoPlayer._logCreatePromise = (async () => {
      const logId = await createAdvertisementLogWithRetries(adId);
      if (logId != null) vimeoPlayer.logId = logId;
    })().finally(() => {
      vimeoPlayer._logCreatePromise = null;
    });
  }

  await vimeoPlayer._logCreatePromise;
}

async function readPlayedTotalsFromVimeo(vimeoPlayer) {
  let played;
  try {
    played = await vimeoPlayer.getPlayed();
  } catch (e) {
    return null;
  }
  if (played == null || played.length === 0) return null;

  const durations = played.map((range) => range[1] - range[0]);
  const endPoints = played.map((range) => range[1]);
  const duration = durations.reduce((sum, d) => sum + d, 0);
  const currentTime = Math.max(...endPoints);

  if (duration < 0 || !Number.isFinite(duration) || !Number.isFinite(currentTime)) return null;

  return { duration, currentTime };
}

async function persistAdvertisementLogPlayback(vimeoPlayer, totals) {
  const { logId } = vimeoPlayer;
  if (logId == null) return;

  await fetchAdvertisementLogResource('PUT', `/api/log_advertisements/${logId}`, {
    duration: totals.duration,
    current_time: totals.currentTime,
  });
}

export async function flushAdvertisementVideoLogPlayback(vimeoPlayer) {
  if (vimeoPlayer.logId == null) return;

  // Serialize all flush entry points (interval, pause, modal close) so two PUTs never overlap.
  const prev = vimeoPlayer._logFlushChain ?? Promise.resolve();
  const done = prev.then(async () => {
    try {
      const totals = await readPlayedTotalsFromVimeo(vimeoPlayer);
      if (totals == null) return;

      await persistAdvertisementLogPlayback(vimeoPlayer, totals);
    } catch (e) {
      // Swallow errors so playback UI and modal flow are not blocked by logging failures.
    }
  });
  vimeoPlayer._logFlushChain = done.catch(() => {});
  await done;
}

export function clearAdvertisementVideoLogFlushTimer(vimeoPlayer) {
  if (!vimeoPlayer?.logFlushTimerId) return;

  clearInterval(vimeoPlayer.logFlushTimerId);
  vimeoPlayer.logFlushTimerId = null;
}

/**
 * Wire Vimeo `play` / `pause` + interval flushes to log_advertisements (video modal only).
 *
 * @param {*} vimeoPlayer Vimeo Player instance
 * @param {string|number} adId `Advertisement` id (same as modal `data-advertisement-id`)
 */
export function registerAdvertisementVideoPlaybackLog(vimeoPlayer, adId) {
  ensureAdvertisementLogPageHideListeners();
  advertisementLogPageContext.activeVimeoPlayer = vimeoPlayer;

  vimeoPlayer.logId = null;
  vimeoPlayer.logFlushTimerId = null;
  vimeoPlayer._logCreatePromise = null;
  vimeoPlayer._logFlushChain = null;

  vimeoPlayer.on('play', async () => {
    await ensureVideoLogRowOnPlayer(vimeoPlayer, adId);
    clearAdvertisementVideoLogFlushTimer(vimeoPlayer);
    vimeoPlayer.logFlushTimerId = setInterval(() => {
      void flushAdvertisementVideoLogPlayback(vimeoPlayer);
    }, LOG_FLUSH_INTERVAL_MS);
  });

  vimeoPlayer.on('pause', async () => {
    clearAdvertisementVideoLogFlushTimer(vimeoPlayer);
    await flushAdvertisementVideoLogPlayback(vimeoPlayer);
  });
}

/**
 * Clear pagehide target when modal closes (video player only).
 *
 * @param {*} vimeoPlayer Vimeo Player instance
 */
export function releaseAdvertisementLogPageContextIfCurrent(vimeoPlayer) {
  if (advertisementLogPageContext.activeVimeoPlayer === vimeoPlayer) {
    advertisementLogPageContext.activeVimeoPlayer = null;
  }
}
